import { Injectable, inject, signal } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { ApiRequests } from 'src/app/shared/types/api.type';
import { BackendService } from 'src/app/services/backend.service';

const DB_NAME = 'yard-claim-outbox';
const DB_VERSION = 1;
const STORE = 'entries';

export interface YardClaimOutboxFile {
  name: string;
  mimeType: string;
  buffer: ArrayBuffer;
}

/** Which record an entry becomes on sync. Absent on entries written before SHIP-1911. */
export type YardOutboxKind = 'claim' | 'baubericht';

export interface YardClaimOutboxEntry {
  id: string;
  kind?: YardOutboxKind;
  mimeType?: string;
  subject?: string;
  description?: string;
  siteId?: number;
  locationCode?: string;
  locationLabel?: string;
  audioBuffer?: ArrayBuffer;
  files?: YardClaimOutboxFile[];
  /** Id of the record created on sync — the entry's files still have to follow. */
  claimId?: number;
  createdAt: number;
}

export interface YardClaimOutboxEnqueueInput {
  /** Defaults to "claim". */
  kind?: YardOutboxKind;
  blob?: Blob;
  mimeType?: string;
  files?: File[];
  subject?: string;
  description?: string;
  siteId?: number;
  locationCode?: string;
  locationLabel?: string;
}

/**
 * IndexedDB outbox for yard records dictated while offline.
 *
 * Carries claims (recording or a complete typed claim) and, since SHIP-1911, Baubericht
 * recordings, so no take is lost on a ship without a connection. The store name stays
 * `yard-claim-outbox` because entries written by earlier versions still live in it; an entry
 * without a `kind` is a claim.
 */
@Injectable({ providedIn: 'root' })
export class YardClaimOutboxService {
  private readonly backendService = inject(BackendService);
  private dbPromise: Promise<IDBDatabase> | null = null;
  private syncing = false;

  readonly pendingCount = signal(0);

  constructor() {
    if (typeof window === 'undefined') return;
    void this.refreshPendingCount().then(() => {
      if (navigator.onLine) void this.drainOutbox();
    });
    window.addEventListener('online', () => void this.drainOutbox());
  }

  async enqueue(input: YardClaimOutboxEnqueueInput): Promise<void> {
    const kind = input.kind ?? 'claim';
    const hasAudio = !!input.blob;
    // Only a claim can be filed from typed data alone; a Baubericht reaches the outbox solely
    // as a recording, because its form is saved online.
    const hasManualClaim =
      kind === 'claim' &&
      input.siteId !== undefined &&
      !!input.subject?.trim() &&
      !!input.description?.trim() &&
      !!input.locationLabel?.trim();
    if (!hasAudio && !hasManualClaim) {
      throw new Error('Outbox entry requires audio or complete manual claim data');
    }

    const entry: YardClaimOutboxEntry = {
      id: crypto.randomUUID(),
      kind,
      mimeType: input.mimeType,
      subject: input.subject?.trim() || undefined,
      description: input.description?.trim() || undefined,
      siteId: input.siteId,
      locationCode: input.locationCode?.trim() || undefined,
      locationLabel: input.locationLabel?.trim() || undefined,
      audioBuffer: input.blob ? await input.blob.arrayBuffer() : undefined,
      files: input.files?.length ? await this.storeFiles(input.files) : undefined,
      createdAt: Date.now(),
    };
    await this.putEntry(entry);
    await this.refreshPendingCount();
  }

  private async drainOutbox(): Promise<void> {
    if (this.syncing || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
    this.syncing = true;
    try {
      const entries = await this.getAllEntries();
      for (const entry of entries) {
        try {
          await this.syncEntry(entry);
          await this.deleteEntry(entry.id);
        } catch {
          break;
        }
      }
    } finally {
      this.syncing = false;
      await this.refreshPendingCount();
    }
  }

  private async syncEntry(entry: YardClaimOutboxEntry): Promise<void> {
    const kind = entry.kind ?? 'claim';
    let recordId = entry.claimId;
    if (!recordId) {
      recordId = await this.createRecord(entry, kind);
      // The recording is on the server now; dropping it keeps the outbox from re-uploading
      // megabytes if only the file upload below still has to be retried.
      await this.patchEntry(entry.id, { claimId: recordId, audioBuffer: undefined, mimeType: undefined });
      entry = { ...entry, claimId: recordId, audioBuffer: undefined, mimeType: undefined };
    }
    if (entry.files?.length) {
      await this.uploadFiles(recordId, entry.files, kind);
    }
  }

  private async createRecord(entry: YardClaimOutboxEntry, kind: YardOutboxKind): Promise<number> {
    if (!entry.audioBuffer) return this.syncCreateClaim(entry);
    return kind === 'baubericht' ? this.syncBauberichtIntake(entry) : this.syncIntake(entry);
  }

  private audioFormData(entry: YardClaimOutboxEntry, fileBase: string): FormData {
    const normalizedMime = (entry.mimeType ?? 'audio/webm').split(';')[0].trim() || 'audio/webm';
    const extension = normalizedMime.includes('mp4') ? 'm4a' : 'webm';
    const formData = new FormData();
    formData.append('audio', new Blob([entry.audioBuffer!], { type: normalizedMime }), `${fileBase}.${extension}`);
    if (entry.subject) formData.append('subject', entry.subject);
    if (entry.description) formData.append('description', entry.description);
    if (entry.siteId !== undefined) formData.append('siteId', String(entry.siteId));
    return formData;
  }

  private async syncIntake(entry: YardClaimOutboxEntry): Promise<number> {
    const formData = this.audioFormData(entry, 'claim-voice');
    if (entry.locationCode) formData.append('locationCode', entry.locationCode);
    if (entry.locationLabel) formData.append('locationLabel', entry.locationLabel);

    const result = await firstValueFrom(
      this.backendService.upload<'yard/intakeClaim'>('yard/claims/intake', { body: formData as never }).pipe(
        filter((event): event is NonNullable<typeof event> => event != null),
        first()
      )
    );
    return result.id;
  }

  private async syncBauberichtIntake(entry: YardClaimOutboxEntry): Promise<number> {
    const result = await firstValueFrom(
      this.backendService
        .upload<'yard/intakeBaubericht'>('yard/bauberichte/intake', {
          body: this.audioFormData(entry, 'baubericht-voice') as never,
        })
        .pipe(
          filter((event): event is NonNullable<typeof event> => event != null),
          first()
        )
    );
    return result.id;
  }

  private async syncCreateClaim(entry: YardClaimOutboxEntry): Promise<number> {
    const body: ApiRequests['yard/createClaim'] = {
      siteId: entry.siteId,
      subject: entry.subject,
      description: entry.description,
      locationCode: entry.locationCode,
      locationLabel: entry.locationLabel!,
    };
    const claim = await firstValueFrom(this.backendService.post<'yard/createClaim'>('yard/claims', { body }));
    return claim.id;
  }

  private async uploadFiles(recordId: number, files: YardClaimOutboxFile[], kind: YardOutboxKind): Promise<void> {
    const nonceRes = await firstValueFrom(
      this.backendService.post<'auth/generateNonce'>('auth/{id}/nonce', { path: { id: recordId } })
    );
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', new Blob([file.buffer], { type: file.mimeType }), file.name);
    }
    // The two routes answer with different file shapes; nothing here reads the answer, so the
    // common `unknown` keeps the union pipeable.
    const upload$: Observable<unknown> =
      kind === 'baubericht'
        ? this.backendService.upload<'yard/uploadBauberichtFiles'>('yard/bauberichte/{id}/files', {
            path: { id: recordId.toString() },
            body: formData as never,
            query: { nonce: nonceRes.nonce },
          })
        : this.backendService.upload<'yard/uploadClaimFiles'>('yard/claims/{id}/files', {
            path: { id: recordId.toString() },
            body: formData as never,
            query: { nonce: nonceRes.nonce },
          });
    await firstValueFrom(upload$.pipe(filter((event) => event != null), first()));
  }

  private async storeFiles(files: File[]): Promise<YardClaimOutboxFile[]> {
    return Promise.all(
      files.map(async (file) => ({
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        buffer: await file.arrayBuffer(),
      }))
    );
  }

  private openDb(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(STORE)) {
            db.createObjectStore(STORE, { keyPath: 'id' });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
      });
    }
    return this.dbPromise;
  }

  private async putEntry(entry: YardClaimOutboxEntry): Promise<void> {
    const db = await this.openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async patchEntry(id: string, patch: Partial<YardClaimOutboxEntry>): Promise<void> {
    const db = await this.openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const request = store.get(id);
      request.onsuccess = () => {
        const current = request.result as YardClaimOutboxEntry | undefined;
        if (!current) {
          reject(new Error('Outbox entry not found'));
          return;
        }
        store.put({ ...current, ...patch, id });
      };
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async deleteEntry(id: string): Promise<void> {
    const db = await this.openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async getAllEntries(): Promise<YardClaimOutboxEntry[]> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const request = tx.objectStore(STORE).getAll();
      request.onsuccess = () =>
        resolve((request.result as YardClaimOutboxEntry[]).sort((a, b) => a.createdAt - b.createdAt));
      request.onerror = () => reject(request.error);
    });
  }

  private async refreshPendingCount(): Promise<void> {
    try {
      const entries = await this.getAllEntries();
      this.pendingCount.set(entries.length);
    } catch {
      this.pendingCount.set(0);
    }
  }
}
